import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import VehicleIdSelect from "components/Common/VehicleIdSelect"
import VoucherImageUpload from "components/Common/VoucherImageUpload"
import { useMemo, useCallback, useState, useEffect } from "react"
import { toast } from "react-toastify"
import { Form, FormFeedback, Label, Button, Row, Col, Card, CardBody } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import { useQuery } from "@tanstack/react-query"
import { useCustomers } from "hooks/useCustomer"
import { getUsers } from "api/userApi"
import { useAccountPaymentMethods } from "hooks/useAccountPaymentMethod"
import { useAccounts } from "hooks/useAccountEntity"
import {
  useCreateAccountCustomerRefund,
  useEditAccountCustomerRefund,
} from "hooks/useAccountCustomerRefund"

const AccountCustomerRefundAddEdit = ({ modal, toggleModal, isEdit, editedData }) => {
  const { mutateAsync: createMutation, isPending: createLoading } = useCreateAccountCustomerRefund()
  const { mutateAsync: editMutation, isPending: editLoading } = useEditAccountCustomerRefund()

  const [voucherImages, setVoucherImages] = useState(editedData?.images || [])

  useEffect(() => {
    setVoucherImages(editedData?.images || [])
  }, [editedData])

  const { data: customerResponse, isLoading: customerLoading } = useCustomers(
    { all: true, per_page: 200 },
    modal
  )
  const { data: paymentMethodResponse, isLoading: paymentMethodLoading } =
    useAccountPaymentMethods({ all: true, per_page: 200 }, modal)
  const { data: accountResponse, isLoading: accountLoading } =
    useAccounts({ all: true, per_page: 200 }, modal)

  const { data: userResponse, isLoading: userLoading } = useQuery({
    queryKey: ["users", "account-customer-refund"],
    queryFn: () => getUsers({ all: true, per_page: 200 }),
    enabled: modal,
  })

  const customers = customerResponse?.data?.data || customerResponse?.data || []
  const paymentMethods = paymentMethodResponse?.data?.data || paymentMethodResponse?.data || []
  const accounts = accountResponse?.data?.data || accountResponse?.data || []
  const users = userResponse?.data?.data || userResponse?.data || []

  const customerOptions = useMemo(
    () => (Array.isArray(customers) ? customers.map(item => ({ value: item.id, label: item.name })) : []),
    [customers]
  )

  const paymentMethodOptions = useMemo(
    () =>
      Array.isArray(paymentMethods)
        ? paymentMethods.map(item => ({ value: item.id, label: item.name }))
        : [],
    [paymentMethods]
  )

  const accountOptions = useMemo(
    () =>
      Array.isArray(accounts)
        ? accounts.map(item => ({
            value: item.id,
            label: item.custodian?.name ? `${item.name} (${item.custodian.name})` : item.name,
          }))
        : [],
    [accounts]
  )

  const userOptions = useMemo(
    () => (Array.isArray(users) ? users.map(item => ({ value: item.id, label: item.name })) : []),
    [users]
  )

  const validationSchema = Yup.object({
    customer_id: Yup.mixed().required("Please select a customer"),
    vehicle_id: Yup.mixed().nullable(),
    amount: Yup.number()
      .typeError("Amount must be numeric")
      .integer("Amount must be a whole number")
      .min(1, "Amount must be at least 1")
      .required("Please enter refund amount"),
    payment_method_id: Yup.mixed().required("Please select a payment method"),
    account_id: Yup.mixed().required("Please select the account refunding from"),
    refunded_by: Yup.mixed().nullable(),
    refund_date: Yup.string().required("Please select refund date"),
    reason: Yup.string().nullable().max(255, "Reason cannot exceed 255 characters"),
    notes: Yup.string().nullable(),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        ...values,
        vehicle_id: values.vehicle_id?.value ?? values.vehicle_id ?? null,
        amount: Number(values.amount),
        reason: values.reason?.trim() || null,
        notes: values.notes?.trim() || null,
        image_ids: voucherImages.map(img => img.id),
      }

      const action = isEdit
        ? editMutation({ id: editedData.id, ...payload })
        : createMutation(payload)

      try {
        const res = await action
        toast.success(res?.message ?? `Customer refund ${isEdit ? "updated" : "created"} successfully.`)
        resetForm()
        toggleModal()
      } catch (error) {
        const msg = error?.response?.data?.message || "Failed to save customer refund."
        toast.error(msg)
      }
    },
    [isEdit, editedData, editMutation, createMutation, toggleModal, voucherImages]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      customer_id: editedData?.customer_id || editedData?.customer?.id || "",
      vehicle_id: editedData?.vehicle
        ? {
            label: `${editedData.vehicle.stock_id || ""} - ${editedData.vehicle.full_chassis || ""}`,
            value: editedData.vehicle.id,
          }
        : editedData?.vehicle_id || "",
      amount: editedData?.amount ?? "",
      payment_method_id: editedData?.payment_method_id || editedData?.paymentMethod?.id || "",
      account_id: editedData?.account_id || editedData?.account?.id || "",
      refunded_by: editedData?.refunded_by?.id || editedData?.refunded_by || "",
      refund_date: editedData?.refund_date || "",
      reason: editedData?.reason || "",
      notes: editedData?.notes || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const loadingAny =
    createLoading ||
    editLoading ||
    customerLoading ||
    paymentMethodLoading ||
    accountLoading ||
    userLoading

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Customer Refund" : "Add Customer Refund"}
      size="xl"
    >
      <Form onSubmit={e => { e.preventDefault(); formik.handleSubmit() }}>
        <Card className="border">
          <CardBody>
            <h5 className="mb-3">Refund Information</h5>
            <Row>
              <Col md={6}>
                <FormikCreatableSelect name="customer_id" formik={formik} label="Customer" options={customerOptions} loading={customerLoading} required />
              </Col>
              <Col md={6}>
                <VehicleIdSelect formik={formik} />
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <div className="mb-3">
                  <Label htmlFor="amount">Refund Amount</Label>
                  <InputField type="number" name="amount" id="amount" min="1" value={formik.values.amount} onChange={formik.handleChange} invalid={formik.touched.amount && !!formik.errors.amount} />
                  {formik.touched.amount && formik.errors.amount && <FormFeedback type="invalid">{formik.errors.amount}</FormFeedback>}
                </div>
              </Col>
              <Col md={4}>
                <FormikCreatableSelect name="payment_method_id" formik={formik} label="Payment Method" options={paymentMethodOptions} loading={paymentMethodLoading} required />
              </Col>
              <Col md={4}>
                <FormikCreatableSelect name="account_id" formik={formik} label="Refund From Account" options={accountOptions} loading={accountLoading} required />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="refund_date">Refund Date</Label>
                  <InputField type="date" name="refund_date" id="refund_date" value={formik.values.refund_date} onChange={formik.handleChange} invalid={formik.touched.refund_date && !!formik.errors.refund_date} />
                  {formik.touched.refund_date && formik.errors.refund_date && <FormFeedback type="invalid">{formik.errors.refund_date}</FormFeedback>}
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="reason">Reason</Label>
                  <InputField type="text" name="reason" id="reason" value={formik.values.reason} onChange={formik.handleChange} invalid={formik.touched.reason && !!formik.errors.reason} />
                  {formik.touched.reason && formik.errors.reason && <FormFeedback type="invalid">{formik.errors.reason}</FormFeedback>}
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card className="border mt-3">
          <CardBody>
            <h5 className="mb-3">Additional Details</h5>
            <Row>
              <Col md={6}>
                <FormikCreatableSelect name="refunded_by" formik={formik} label="Refunded By" options={userOptions} loading={userLoading} />
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="notes">Notes</Label>
                  <InputField type="textarea" name="notes" id="notes" value={formik.values.notes} onChange={formik.handleChange} invalid={formik.touched.notes && !!formik.errors.notes} />
                  {formik.touched.notes && formik.errors.notes && <FormFeedback type="invalid">{formik.errors.notes}</FormFeedback>}
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <VoucherImageUpload images={voucherImages} onChange={setVoucherImages} />
              </Col>
            </Row>
          </CardBody>
        </Card>

        <div className="text-end mt-3">
          <Button color="success" type="submit" disabled={loadingAny}>
            {loadingAny ? "Saving..." : isEdit ? "Save Changes" : "Add Refund"}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default AccountCustomerRefundAddEdit
